import { useEffect, useState } from "react";
import { AGENT_ID, agentIdFrom, decodePart } from "./base";
import type { PanelEntry } from "./api";
import type { OwnerPending } from "../shell/ownerShell";

const NAVIGATE = "pa:studio:navigate";
export const studioFrame = () => new URLSearchParams(location.search).get("workspace") === "studio";
/** The frames the Hub keeps an Agent page in (`hub/agentFrame.ts`). */
const MANAGED = ["studio", "settings", "embedded"];
const managedFrame = () => MANAGED.includes(new URLSearchParams(location.search).get("workspace") || "");
/** Frames whose pages are the Hub's addresses: the Hub routes them, and their links are the Hub's. */
const HUB_ROUTED = ["studio", "settings"];

/** Only application pages participate; assets, downloads and external links stay native. */
export function studioUrl(href: string, base = location.href): URL | null {
  try {
    const url = new URL(href, base);
    if (url.origin !== location.origin || (url.hash && !url.hash.startsWith("#/"))) return null;
    if (url.pathname !== "/" && !/^\/agents\/[^/]+\/?$/.test(url.pathname)) return null;
    // A malformed Agent id is not an application page.
    if (url.pathname !== "/" && decodePart(url.pathname.split("/")[2]) === null) return null;
    if (HUB_ROUTED.includes(url.searchParams.get("workspace") || "")) url.searchParams.delete("workspace");
    if (url.search) return null;
    return url;
  } catch { return null; }
}

/**
 * Go to an application page.
 *
 * True when the page above this frame carries it out: the route comes back
 * from there, and a caller that drew it now would draw a page the Hub may send
 * somewhere else (an Agent's settings open in the Hub's 设置).
 */
export function navigateWorkbench(href: string, replace = false): boolean {
  const url = studioUrl(href);
  if (managedFrame() && parent !== window && url) {
    parent.postMessage({ type: "pa.studio.navigate", href: url.href, replace }, location.origin);
    return true;
  }
  const event = new CustomEvent(NAVIGATE, { cancelable: true, detail: { href, replace } });
  if (!window.dispatchEvent(event)) return false;
  if (replace) history.replaceState(null, "", href);
  else if (href.startsWith("#")) location.hash = href;
  else location.assign(href);
  return false;
}

function linkDestination(event: MouseEvent): string | null {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const anchor = (event.target as Element)?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!anchor || anchor.hasAttribute("download") || (anchor.target && !["_self", "_top"].includes(anchor.target))) return null;
  return studioUrl(anchor.href)?.href ?? null;
}

function currentAddress() { return `${location.pathname}${location.search}${location.hash}`; }

function visibleFrame(frame: HTMLIFrameElement) {
  for (let element: Element | null = frame; element; element = element.parentElement)
    if (element.hasAttribute("hidden") || getComputedStyle(element).display === "none") return false;
  return true;
}

/** A user step may recover a sleeping workspace; route replay and observation may not. */
export function useWorkspaceNavigationWake(recover: () => void) {
  useEffect(() => {
    const clicked = (event: MouseEvent) => {
      const href = linkDestination(event);
      if (href && agentIdFrom(new URL(href).pathname) === AGENT_ID) recover();
    };
    const receive = (event: MessageEvent) => {
      if (managedFrame() && parent !== window && event.source === parent && event.origin === location.origin
        && event.data?.type === "pa.workspace.wake") recover();
    };
    // Before the router handles the anchor, including a reselected current page.
    document.addEventListener("click", clicked, true);
    window.addEventListener("message", receive);
    return () => {
      document.removeEventListener("click", clicked, true);
      window.removeEventListener("message", receive);
    };
  }, [recover]);
}

/** The Hub alone owns browser history. Agent documents retain their immutable API scope. */
export function useStudioNavigation() {
  const [address, setAddress] = useState(currentAddress);
  useEffect(() => {
    const changed = () => setAddress(currentAddress());
    const requested = (event: Event) => {
      const { href, replace } = (event as CustomEvent).detail ?? {};
      if (typeof href !== "string") return;
      const url = studioUrl(href);
      if (!url) return;
      event.preventDefault();
      const next = `${url.pathname}${url.hash}`;
      if (next === currentAddress()) return;
      history[replace ? "replaceState" : "pushState"](null, "", next);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    };
    const clicked = (event: MouseEvent) => {
      const href = linkDestination(event);
      if (!href) return;
      event.preventDefault();
      navigateWorkbench(href);
    };
    const wakeWorkspace = (event: MouseEvent) => {
      const href = linkDestination(event);
      if (!href) return;
      const target = agentIdFrom(new URL(href).pathname);
      if (!target) return;
      for (const frame of document.querySelectorAll("iframe")) {
        const source = new URL(frame.src, location.href);
        if (source.origin === location.origin && source.searchParams.get("workspace") === "studio"
          && agentIdFrom(source.pathname) === target && visibleFrame(frame))
          frame.contentWindow?.postMessage({ type: "pa.workspace.wake" }, location.origin);
      }
    };
    const fromEmbeddedPage = (event: MessageEvent) => {
      if (event.origin !== location.origin || event.data?.type !== "pa.studio.navigate" || typeof event.data.href !== "string") return;
      const frame = [...document.querySelectorAll("iframe")].find(frame => frame.contentWindow === event.source);
      // Any Agent page the Hub keeps in a frame, and only while the owner can see it.
      if (!frame || !MANAGED.includes(new URL(frame.src, location.href).searchParams.get("workspace") || "")) return;
      if (!visibleFrame(frame)) return;
      const url = studioUrl(event.data.href);
      if (url) navigateWorkbench(url.href, event.data.replace === true);
    };
    window.addEventListener(NAVIGATE, requested);
    window.addEventListener("popstate", changed);
    window.addEventListener("hashchange", changed);
    document.addEventListener("click", clicked);
    document.addEventListener("click", wakeWorkspace, true);
    window.addEventListener("message", fromEmbeddedPage);
    return () => {
      window.removeEventListener(NAVIGATE, requested);
      window.removeEventListener("popstate", changed);
      window.removeEventListener("hashchange", changed);
      document.removeEventListener("click", clicked);
      document.removeEventListener("click", wakeWorkspace, true);
      window.removeEventListener("message", fromEmbeddedPage);
    };
  }, []);
  const url = new URL(address, location.origin);
  return { hash: url.hash, agentId: agentIdFrom(url.pathname), address };
}

/** Frame routes replace their own history so Back/Forward has one entry per user action. */
export function useStudioFrameRouting() {
  useEffect(() => {
    if (!managedFrame() || parent === window) return;
    const receive = (event: MessageEvent) => {
      if (!HUB_ROUTED.includes(new URLSearchParams(location.search).get("workspace") || "")
        || event.origin !== location.origin || event.source !== parent || event.data?.type !== "pa.studio.route") return;
      const hash = event.data.hash;
      if (typeof hash !== "string" || !hash.startsWith("#/") || hash === location.hash) return;
      history.replaceState(null, "", hash);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    };
    const clicked = (event: MouseEvent) => {
      const href = linkDestination(event);
      if (!href) return;
      event.preventDefault();
      navigateWorkbench(href);
    };
    window.addEventListener("message", receive);
    document.addEventListener("click", clicked);
    return () => {
      window.removeEventListener("message", receive);
      document.removeEventListener("click", clicked);
    };
  }, []);
}

export interface StudioWorkspaceState {
  ready: boolean;
  hash?: string;
  panelKey?: string;
  sessionId?: string;
  panels: PanelEntry[];
  version: string;
  pendingCount: number;
  /** The visible Agent's own confirmed requests; absent on older documents. */
  pending?: OwnerPending;
}
